import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, CardHeader, StatusBadge, useFocusTrap } from '@truecare/ui';
import { unwrap } from '@truecare/api-client';
import { api } from '../api';
import { formatDate } from '../lib/format-date';

type BuyerPersona = 'facility' | 'homecare';

// ── shapes (portal read views — /me/{persona}/invoices) ─────────────────────
interface InvoiceListItem {
  id: string;
  invoiceNumber: string | null;
  status: string;
  subtotalCents: number;
  hstCents: number;
  totalCents: number;
  amountPaidCents: number;
  cycleStartDate: string;
  cycleEndDate: string;
  issuedAt: string | null;
  dueDate: string | null;
  paidAt: string | null;
  voidedAt: string | null;
  lineCount: number;
}
interface InvoiceListResponse {
  items: InvoiceListItem[];
  totalCount: number;
}
interface InvoiceLine {
  id: string;
  lineType: string;
  description: string;
  quantityHours: string | number | null;
  totalCents: number;
}
interface PaymentEvent {
  id: string;
  eventType: string;
  amountCents: number;
  externalRef: string | null;
  recordedAt: string;
}
interface DunningEvent {
  id: string;
  stepNumber: number;
  templateKey: string;
  outcome: string;
  dispatchedAt: string | null;
}
interface InvoiceDetail extends InvoiceListItem {
  lines: InvoiceLine[];
  paymentEvents: PaymentEvent[];
  dunningEvents: DunningEvent[];
  /** Short-TTL signed download URL for the rendered invoice PDF (facility invoices
   *  only; null until the render-invoice-pdf job has run). */
  pdfUrl: string | null;
}

function money(cents: number): string {
  return (cents / 100).toLocaleString('en-CA', {
    style: 'currency',
    currency: 'CAD',
  });
}
// Fix-verify MED — delegate to the shared tz-safe formatDate. The old local
// `new Date(iso).toLocaleDateString()` rendered date-only strings
// (cycleStartDate / cycleEndDate / dueDate = "YYYY-MM-DD") one day early for any
// Toronto (UTC-4/5) buyer, on every invoice row + the detail modal. formatDate
// pins date-only strings to local midnight while keeping full datetimes correct.
function shortDate(iso: string | null): string {
  return formatDate(iso);
}

// Audit M4 (2026-09-01) — homecare invoices intentionally keep cycleStart/End
// NULL (per-visit, not per-cycle), so a bare `{start} – {end}` rendered a
// dangling "— – —" that reads as broken data on a billing surface. Show a real
// label instead: the biweekly range for facility invoices, the issue date (or
// "Per-visit charge") for homecare.
function periodLabel(inv: {
  cycleStartDate: string | null;
  cycleEndDate: string | null;
  issuedAt: string | null;
}): string {
  if (inv.cycleStartDate && inv.cycleEndDate) {
    return `${shortDate(inv.cycleStartDate)} – ${shortDate(inv.cycleEndDate)}`;
  }
  return inv.issuedAt ? `Issued ${shortDate(inv.issuedAt)}` : 'Per-visit charge';
}

// Invoice.status is payment-state (ISSUED / PARTIAL / PAID …); "overdue" is a
// time-state the backend derives from dueDate rather than persisting, so the
// badge must derive it too — otherwise a past-due invoice shows a calm green
// ISSUED pill. An open invoice is overdue once the calendar due date has passed.
function effectiveStatus(inv: { status: string; dueDate: string | null }): string {
  if ((inv.status === 'ISSUED' || inv.status === 'PARTIAL') && inv.dueDate) {
    const todayIso = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD, local
    if (inv.dueDate.slice(0, 10) < todayIso) return 'OVERDUE';
  }
  return inv.status;
}

// NOTE: the open/closed split is no longer computed here. It moved server-side
// (`?state=open|closed`) in the 2026-07-23 audit fix for U1 — filtering a
// single truncated page in the browser is what let an OVERDUE invoice hide
// behind 20 PAID rows while this tab said "you're all paid up".

// Persona-specific copy (facility AR is operator-recorded; homecare is
// auto-charged to the card on file).
const COPY = {
  facility: {
    description:
      'Your biweekly statements. Payments are recorded by TrueCare™ when received — reach out to your account manager for remittance details.',
    empty:
      'No invoices yet. Your first statement is generated after your first billing cycle closes.',
  },
  homecare: {
    description:
      'Your statements. Charges are applied automatically to the card on file — no action needed.',
    empty: 'No invoices yet. Your first statement is generated after your first booking.',
  },
} as const;

/** Trailing "this row opens something" cue — persistent (visible at rest and
 *  on touch), not the hover-only border. */
function ChevronRight() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      className="h-4 w-4 shrink-0 text-ink-muted"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M7 5l5 5-5 5" />
    </svg>
  );
}

function InvoiceRow({
  inv,
  onSelect,
}: {
  inv: InvoiceListItem;
  onSelect: (id: string) => void;
}) {
  const balance = inv.totalCents - inv.amountPaidCents;
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(inv.id)}
        className="flex w-full flex-wrap items-center justify-between gap-x-6 gap-y-1 rounded-md border border-border px-4 py-3 text-left transition-colors hover:border-forest hover:bg-paper-warm"
        data-testid="buyer-invoice-row"
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-body text-sm font-semibold text-ink">
              {inv.invoiceNumber ?? 'Draft'}
            </span>
            <StatusBadge status={effectiveStatus(inv)} />
          </div>
          <div className="font-body text-xs text-ink-muted">
            {periodLabel(inv)}
            {inv.dueDate ? ` · due ${shortDate(inv.dueDate)}` : ''}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="font-body text-sm font-semibold text-ink tabular-nums">
              {money(inv.totalCents)}
            </div>
            {inv.status === 'VOIDED' ? (
              <div className="font-body text-xs text-ink-muted tabular-nums">
                Voided — no balance due
              </div>
            ) : balance > 0 && inv.status !== 'DRAFT' ? (
              <div className="font-body text-xs text-ink-muted tabular-nums">
                {money(balance)} outstanding
              </div>
            ) : null}
          </div>
          {/* Persistent affordance — the whole row opens a detail modal; a
              hover-only border is invisible on touch. */}
          <ChevronRight />
        </div>
      </button>
    </li>
  );
}

function InvoiceSection({
  title,
  invoices,
  onSelect,
  emptyNote,
}: {
  title: string;
  invoices: InvoiceListItem[];
  onSelect: (id: string) => void;
  emptyNote?: string;
}) {
  if (invoices.length === 0 && !emptyNote) return null;
  return (
    <section>
      <h3 className="font-heading text-base text-forest">{title}</h3>
      {invoices.length === 0 ? (
        <p className="mt-2 font-body text-sm text-ink-muted">{emptyNote}</p>
      ) : (
        <ul className="mt-2 flex flex-col gap-2">
          {invoices.map((inv) => (
            <InvoiceRow key={inv.id} inv={inv} onSelect={onSelect} />
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Buyer "Invoices" tab — read-only view of the AR a facility or homecare client
 * owes. Both rails share the portal billing read shape; the buyer never pays
 * in-app (facility AR is operator-recorded; homecare is auto-charged to the card
 * on file). Transparency: what's owed, what's been paid, and the reminder trail.
 */
/** Page size for the closed-invoice history. Outstanding is fetched whole. */
const CLOSED_PAGE_SIZE = 20;

export function BuyerInvoicesTab({
  persona,
  // Audit L3 (2026-09-01) — on the homecare Billing tab this card is STACKED
  // under Payment methods, so per the Card all-or-none rail rule it must also be
  // railed (gold = billing). Solo use (facility Invoices tab) leaves it plain.
  railed = false,
}: {
  persona: BuyerPersona;
  railed?: boolean;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [closedPage, setClosedPage] = useState(1);

  const listPath =
    persona === 'facility'
      ? ('/api/v1/me/facility/invoices' as const)
      : ('/api/v1/me/homecare/invoices' as const);

  // 2026-07-23 pre-deploy audit (U1) — TWO server-filtered queries instead of
  // one unfiltered page split client-side. Previously this component fetched a
  // single default page (20 rows, ordered `status: 'asc'` = enum declaration
  // order, in which PAID precedes OVERDUE) and filtered it in the browser, so a
  // facility with 20+ paid invoices was told "you're all paid up" while an
  // overdue invoice sat on a page the UI could not reach.
  //
  // Outstanding is fetched at the max page size and never paginated: it is the
  // set the buyer must act on, so it must never be silently truncated. (If a
  // buyer ever exceeds 100 open invoices, the count banner below makes that
  // visible rather than hiding it.)
  const openQuery = useQuery({
    queryKey: ['invoices', persona, 'open'],
    queryFn: () => unwrap(api.GET(listPath, { params: { query: { state: 'open', pageSize: 100 } } })),
  });
  const closedQuery = useQuery({
    queryKey: ['invoices', persona, 'closed', closedPage],
    queryFn: () =>
      unwrap(
        api.GET(listPath, {
          params: { query: { state: 'closed', page: closedPage, pageSize: CLOSED_PAGE_SIZE } },
        }),
      ),
  });

  const open = openQuery.data as unknown as InvoiceListResponse | undefined;
  const closed = closedQuery.data as unknown as InvoiceListResponse | undefined;
  const isLoading = openQuery.isLoading || closedQuery.isLoading;
  const isError = openQuery.isError || closedQuery.isError;
  const totalInvoices = (open?.totalCount ?? 0) + (closed?.totalCount ?? 0);
  const closedPageCount = Math.max(1, Math.ceil((closed?.totalCount ?? 0) / CLOSED_PAGE_SIZE));

  return (
    <div className="flex flex-col gap-6">
      <Card padding="lg" {...(railed ? { accent: 'left' as const, tone: 'gold' as const } : {})}>
        <CardHeader title="Invoices" description={COPY[persona].description} />

        {isLoading && (
          <p className="mt-4 font-body text-sm text-ink-muted">Loading invoices…</p>
        )}
        {isError && (
          <Alert tone="danger" className="mt-4">
            Couldn't load invoices. Please refresh the page.
          </Alert>
        )}
        {!isLoading && !isError && totalInvoices === 0 && (
          <p className="mt-4 font-body text-sm text-ink-muted">{COPY[persona].empty}</p>
        )}

        {!isLoading && !isError && totalInvoices > 0 && (
          <div className="mt-4 flex flex-col gap-6">
            <InvoiceSection
              title="Outstanding"
              emptyNote="Nothing outstanding — you're all paid up."
              invoices={open?.items ?? []}
              onSelect={setSelectedId}
            />
            <div className="flex flex-col gap-3">
              <InvoiceSection
                title="Paid & closed"
                invoices={closed?.items ?? []}
                onSelect={setSelectedId}
              />
              {closedPageCount > 1 && (
                <div className="flex items-center justify-between gap-4">
                  <p className="font-body text-xs text-ink-muted">
                    Showing {closed?.items.length ?? 0} of {closed?.totalCount ?? 0} · page{' '}
                    {closedPage} of {closedPageCount}
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="rounded-md border border-border px-3 py-1.5 font-body text-sm text-ink transition-colors hover:border-forest disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={closedPage <= 1}
                      onClick={() => setClosedPage((n) => Math.max(1, n - 1))}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      className="rounded-md border border-border px-3 py-1.5 font-body text-sm text-ink transition-colors hover:border-forest disabled:cursor-not-allowed disabled:opacity-50"
                      disabled={closedPage >= closedPageCount}
                      onClick={() => setClosedPage((n) => Math.min(closedPageCount, n + 1))}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Card>

      {selectedId && (
        <InvoiceDetailModal
          persona={persona}
          invoiceId={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

function InvoiceDetailModal({
  persona,
  invoiceId,
  onClose,
}: {
  persona: BuyerPersona;
  invoiceId: string;
  onClose: () => void;
}) {
  const detailQuery = useQuery({
    queryKey: ['invoices', persona, invoiceId],
    queryFn: () =>
      persona === 'facility'
        ? unwrap(
            api.GET('/api/v1/me/facility/invoices/{id}', {
              params: { path: { id: invoiceId } },
            }),
          )
        : unwrap(
            api.GET('/api/v1/me/homecare/invoices/{id}', {
              params: { path: { id: invoiceId } },
            }),
          ),
  });
  const inv = detailQuery.data as unknown as InvoiceDetail | undefined;

  // audit medium — the invoice modal had NO keyboard handling at all; trap focus
  // + close on Escape via the shared hook.
  const panelRef = useRef<HTMLDivElement>(null);
  useFocusTrap(panelRef, { active: true, onEscape: onClose });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="invoice-detail-title"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/40 p-4"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        className="my-8 w-full max-w-2xl rounded-md border border-border bg-cream p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2
              id="invoice-detail-title"
              className="font-heading text-xl text-forest"
            >
              {inv?.invoiceNumber ?? 'Invoice'}
            </h2>
            {inv && (
              <div className="mt-1 flex items-center gap-2">
                <StatusBadge status={effectiveStatus(inv)} />
                <span className="font-body text-xs text-ink-muted">
                  {periodLabel(inv)}
                </span>
              </div>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {inv?.pdfUrl && (
              <a
                href={inv.pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-md border border-forest px-3 py-1.5 font-body text-sm font-medium text-forest hover:bg-forest hover:text-cream"
                data-testid="invoice-download-pdf"
              >
                Download PDF
              </a>
            )}
            <Button type="button" variant="secondary" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>

        {detailQuery.isLoading && (
          <p className="mt-4 font-body text-sm text-ink-muted">Loading…</p>
        )}
        {detailQuery.isError && (
          <Alert tone="danger" className="mt-4">
            Couldn't load this invoice.
          </Alert>
        )}

        {inv && (
          <div className="mt-5 flex flex-col gap-6">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
              <Meta label="Issued" value={shortDate(inv.issuedAt)} />
              <Meta label="Due" value={shortDate(inv.dueDate)} />
              <Meta label="Paid" value={shortDate(inv.paidAt)} />
              <Meta label="Subtotal" value={money(inv.subtotalCents)} />
              <Meta label="HST" value={money(inv.hstCents)} />
              <Meta label="Total" value={money(inv.totalCents)} strong />
            </dl>

            <section>
              <h3 className="font-heading text-base text-forest">Lines</h3>
              <div className="mt-2 overflow-x-auto">
                <table className="w-full min-w-[28rem] border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-border text-left font-body text-xs uppercase tracking-wide text-ink-muted">
                      <th className="py-1.5 pr-3 font-medium">Description</th>
                      <th className="py-1.5 pr-3 font-medium">Hours</th>
                      <th className="py-1.5 text-right font-medium">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inv.lines.map((l) => (
                      <tr key={l.id} className="border-b border-border-soft last:border-b-0">
                        <td className="py-1.5 pr-3 font-body text-ink">{l.description}</td>
                        <td className="py-1.5 pr-3 font-body text-ink-muted tabular-nums">
                          {l.quantityHours != null ? String(l.quantityHours) : '—'}
                        </td>
                        <td className="py-1.5 text-right font-body text-ink tabular-nums">
                          {money(l.totalCents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {inv.paymentEvents.length > 0 && (
              <section>
                <h3 className="font-heading text-base text-forest">Payments</h3>
                <ul className="mt-2 flex flex-col gap-1.5">
                  {inv.paymentEvents.map((p) => (
                    <li
                      key={p.id}
                      className="flex items-baseline justify-between gap-4 font-body text-sm"
                    >
                      <span className="text-ink">
                        {p.eventType.replaceAll('_', ' ')}
                        {p.externalRef ? ` · ${p.externalRef}` : ''}
                      </span>
                      <span className="text-ink-muted tabular-nums">
                        {money(p.amountCents)} · {shortDate(p.recordedAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {inv.dunningEvents.length > 0 && (
              <section>
                <h3 className="font-heading text-base text-forest">Reminders sent</h3>
                <ul className="mt-2 flex flex-col gap-1.5">
                  {inv.dunningEvents.map((d) => (
                    <li
                      key={d.id}
                      className="flex items-baseline justify-between gap-4 font-body text-sm"
                    >
                      <span className="text-ink">
                        {d.templateKey.replaceAll('_', ' ')}
                      </span>
                      <span className="text-ink-muted">{shortDate(d.dispatchedAt)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Meta({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div>
      <dt className="font-body text-xs font-semibold uppercase tracking-wide text-ink-muted">
        {label}
      </dt>
      <dd
        className={`mt-0.5 font-body text-sm tabular-nums ${strong ? 'font-semibold text-forest' : 'text-ink'}`}
      >
        {value}
      </dd>
    </div>
  );
}
